import classnames from 'classnames';
import sum from 'lodash/sum';
import { arrayOf, func } from 'prop-types';
import React from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalHeader,
} from 'reactstrap';

import CategorySelect from '../../generic/CategorySelect';
import createModalToggle from '../../generic/create-modal-toggle';
import DatePicker from '../../generic/DatePicker';
import MultiSelect from '../../generic/MultiSelect';
import ShareInput from '../../generic/ShareInput';
import { quoteLineShape } from '../../prop-types';
import { genders as allGenders } from '../../quotes/constants';
import { getMomentDate } from '../../utils/date-helpers';
import getEventTargetValue from '../../utils/get-event-target-value';

const allGenderValues = allGenders.map(({ value }) => value);

function genderShareKey(gender) {
  return `gender${gender}Share`;
}

class QuoteLinesBulkEditor extends React.Component {
  state = {
    categories: [],
    genders: [],
    genderShares: {},
    isPartOfCommercialPackage: false,
    startDate: null,
    endDate: null,
  };

  update = event => {
    const value = getEventTargetValue(event.target);
    this.setState({ [event.target.name]: value });
  };

  updateGenderShare = event => {
    const { name, value } = event.target;
    this.setState(({ genderShares }) => ({
      genderShares: { ...genderShares, [name]: value },
    }));
  };

  propagateCategoriesUpdate = () =>
    this.props.onChange('categories', this.state.categories);

  propagateDateUpdate = () => {
    const { startDate, endDate } = this.state;
    if (startDate) {
      this.props.onChange('startDate', startDate);
    }
    if (endDate) {
      this.props.onChange('endDate', endDate);
    }
  };

  propagateGendersUpdate = () => {
    const { onChange } = this.props;
    const { genders, genderShares } = this.state;

    onChange('genders', genders);

    allGenderValues.forEach(gender => {
      const key = genderShareKey(gender);
      onChange(key, genderShares[key] ?? 0);
    });
  };

  propagateIsPartOfCommercialPackageUpdate = () =>
    this.props.onChange(
      'isPartOfCommercialPackage',
      this.state.isPartOfCommercialPackage,
    );

  render() {
    const { quoteLines, toggle } = this.props;
    const {
      categories,
      genders,
      genderShares,
      isPartOfCommercialPackage,
      endDate,
      startDate,
    } = this.state;

    const shareSum = sum(Object.values(genderShares));

    return (
      <>
        <ModalHeader toggle={toggle}>
          Edit the selected quote lines ({quoteLines.length})
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup row className="align-items-center">
              <Label for="bulk.startDate" sm={2}>
                Service Dates
              </Label>
              <Col sm={8}>
                <DatePicker
                  id="bulk.startDate"
                  name="startDate"
                  value={startDate}
                  onChange={this.update}
                />
                <span className="px-1">-</span>
                <DatePicker
                  id="bulk.endDate"
                  name="endDate"
                  value={endDate}
                  onChange={this.update}
                  initialVisibleMonth={() => getMomentDate(startDate)}
                />
              </Col>
              <Col sm={2}>
                <Button onClick={this.propagateDateUpdate}>Update</Button>
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label for="bulk.categories" sm={2}>
                Categories
              </Label>
              <Col sm={8}>
                <CategorySelect
                  id="bulk.categories"
                  name="categories"
                  value={categories}
                  onChange={this.update}
                />
              </Col>
              <Col sm={2}>
                <Button onClick={this.propagateCategoriesUpdate}>Update</Button>
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label for="bulk.genders" sm={2}>
                Genders
              </Label>
              <Col sm={8}>
                <MultiSelect
                  options={allGenders}
                  id="bulk.genders"
                  name="genders"
                  value={genders}
                  onChange={this.update}
                />
              </Col>
              <Col sm={2}>
                <Button onClick={this.propagateGendersUpdate}>Update</Button>
              </Col>
            </FormGroup>
            {genders.length > 0 && (
              <FormGroup row>
                <Col sm={2} />
                <Col sm={8} className="d-flex">
                  <div className="d-inline-block mr-2">
                    ∑
                    <br />
                    <ShareInput
                      className={classnames(
                        'form-control-plaintext-without-color-override-sm',
                        'text-nowrap',
                        {
                          'text-warning': shareSum !== 1,
                        },
                      )}
                      value={shareSum}
                      displayType="text"
                    />
                  </div>
                  {genders.map(gender => {
                    const key = genderShareKey(gender);
                    return (
                      <div key={key} className="d-inline-block mr-2">
                        {gender}
                        <br />
                        <ShareInput
                          bsSize="sm"
                          name={key}
                          value={genderShares[key] ?? 0}
                          onChange={this.updateGenderShare}
                        />
                      </div>
                    );
                  })}
                </Col>
                <Col sm={2} />
              </FormGroup>
            )}
            <FormGroup row>
              <Label sm={2} for="bulk.isPartOfCommercialPackage">
                Part of Commercial Package
              </Label>
              <Col sm={8}>
                <Input
                  id="bulk.isPartOfCommercialPackage"
                  name="isPartOfCommercialPackage"
                  checked={isPartOfCommercialPackage}
                  type="checkbox"
                  className="form-control form-checkbox"
                  onChange={this.update}
                />
              </Col>
              <Col sm={2}>
                <Button onClick={this.propagateIsPartOfCommercialPackageUpdate}>
                  Update
                </Button>
              </Col>
            </FormGroup>
          </Form>
        </ModalBody>
      </>
    );
  }
}

QuoteLinesBulkEditor.propTypes = {
  quoteLines: arrayOf(quoteLineShape.isRequired).isRequired,
  onChange: func.isRequired,
  toggle: func.isRequired,
};

export default createModalToggle(
  'Edit selected lines',
  QuoteLinesBulkEditor,
  { outline: true },
  { size: 'lg' },
);
